import React, { useEffect, useRef, useState } from 'react'
import { FiEdit2, FiCheck } from 'react-icons/fi'
import { StyledCompanyInfoCard } from './NewOnboarding.styles'

const MIN_TEXTAREA_ROWS = 2
const MAX_TEXTAREA_ROWS = 9

const buildValues = (fields) =>
    fields.reduce((acc, field) => ({ ...acc, [field.id]: field.value ?? '' }), {})

const textareaRows = (value) =>
    Math.min(MAX_TEXTAREA_ROWS, Math.max(MIN_TEXTAREA_ROWS, (value || '').split('\n').length + 1))

const FieldEditor = ({ inputRef, multiline, value, onKeyDown, onBlur }) => {
    const sharedProps = { ref: inputRef, defaultValue: value, onKeyDown, onBlur }
    if (multiline) {
        return (
            <textarea
                {...sharedProps}
                className='field-input field-textarea'
                rows={textareaRows(value)}
            />
        )
    }
    return <input {...sharedProps} className='field-input' />
}

const EditableField = ({ field, value, isEditing, inputRef, multiline, onKeyDown, onCommit, onStartEdit }) => (
    <div className='field'>
        <span className='field-label'>{field.label}</span>
        <div className='field-row'>
            {isEditing ? (
                <FieldEditor
                    inputRef={inputRef}
                    multiline={multiline}
                    value={value}
                    onKeyDown={(e) => onKeyDown(e, field.id)}
                    onBlur={(e) => onCommit(field.id, e.target.value)}
                />
            ) : (
                <span className='field-value'>{value}</span>
            )}
            <button
                type='button'
                className='edit-btn'
                onClick={() => {
                    if (isEditing) {
                        onCommit(field.id, inputRef.current?.value ?? value)
                    } else {
                        onStartEdit(field.id)
                    }
                }}
                aria-label={isEditing ? `Save ${field.label}` : `Edit ${field.label}`}
            >
                {isEditing ? <FiCheck /> : <FiEdit2 />}
            </button>
        </div>
    </div>
)

const CompanyInfoCard = ({ fields = [], onChange, multiline = false }) => {
    const [values, setValues] = useState(() => buildValues(fields))
    const [editingId, setEditingId] = useState(null)
    const inputRef = useRef(null)

    useEffect(() => {
        setValues(buildValues(fields))
    }, [fields])

    useEffect(() => {
        if (editingId && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [editingId])

    const startEdit = (id) => setEditingId(id)

    const commit = (id, value) => {
        const trimmed = value.trim()
        setValues(prev => ({ ...prev, [id]: trimmed }))
        setEditingId(null)
        onChange?.(id, trimmed)
    }

    const handleKeyDown = (e, id) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault()
            commit(id, e.currentTarget.value)
        } else if (e.key === 'Escape') {
            setEditingId(null)
        }
    }

    return (
        <StyledCompanyInfoCard className={multiline ? 'multiline' : undefined}>
            {fields?.map(field => (
                <EditableField
                    key={field.id}
                    field={field}
                    value={values[field.id]}
                    isEditing={editingId === field.id}
                    inputRef={inputRef}
                    multiline={multiline}
                    onKeyDown={handleKeyDown}
                    onCommit={commit}
                    onStartEdit={startEdit}
                />
            ))}
        </StyledCompanyInfoCard>
    )
}

export default CompanyInfoCard
